import { designTokens, RiSparklingLine } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import { useCallback, useEffect, useState } from "react";
import { WorkflowLockUnlockModal } from "~/components/common/company_workflow_settings/WorkflowLockUnlockModal";
import { WorkflowStartModal } from "~/components/common/company_workflow_settings/WorkflowStartModal";
import { ConstructionAddForm } from "~/components/common/constructions/adding/ConstructionAddForm";
import { ConstructionAddModal } from "~/components/common/constructions/adding/ConstructionAddModal";
import { ConstructionRequestFooter } from "~/components/common/constructions/ConstructionRequestFooter";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionDownloadButtons } from "~/components/common/constructions/top/ConstructionDownloadButtons";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { DealEstimationOcrModal } from "~/components/specifics/deals/[id]/estimations/edit/DealEstimationOcrModal";
import { useDealEstimationHeaders } from "~/hooks/deals/useDealEstimationHeaders";
import { useDealEstimationHistories } from "~/hooks/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/deals/useDealEstimations";
import { useDealWorkflowRestriction } from "~/hooks/deals/useDealWorkflowRestriction";
import { useCompanyWorkflowSettings } from "~/hooks/useCompanyWorkflowSettings";
import { useDealConstructionItems } from "~/hooks/useDealConstructionItems";
import { useDeals } from "~/hooks/useDeals";
import useItandiAccount from "~/hooks/useItandiAccount";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { NskResponse } from "~/models/Api";
import {
	canStartWorkflow,
	WORKFLOW_LOCK_TARGET_LABEL,
	WORKFLOW_RESOURCE_TYPE
} from "~/models/CompanyWorkflowSetting";
import { ESTIMATION_BY, estimationHeaderStatus } from "~/models/Deal";
import type {
	DealConstruction,
	DealConstructionEdit
} from "~/models/DealConstruction";
import { checkConstructionAddDisabled } from "~/models/DealConstruction";
import { estimationRequestSide } from "~/models/DealEstimationHeader";
import {
	estimationConstructionContext,
	historyConstructionContext
} from "~/models/DealEstimationHistory";
import { buildWorkflowLockMessage } from "~/models/DealWorkflowRestriction";
import {
	canEstimationRequestStatus,
	IN_ESTIMATION,
	isOwnStatus
} from "~/models/Status";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { PATH as ESTIMATIONS_PATH } from "~/pages/deals/[id]/estimations";
import { isSuccess } from "~/utils/api/api_handler";
import { updateSeq as estimationUpdateSeq } from "~/utils/api/deals/DealEstimation";
import { getDownloadPath } from "~/utils/api/deals/DealEstimationFile";
import {
	create,
	destroy,
	update,
	updateRequest
} from "~/utils/api/deals/DealEstimationHeader";
import {
	destroy as destroyEstimationHistory,
	updateSeq as historyUpdateSeq,
	create as postDealEstimationHistory,
	updateTypeDelete
} from "~/utils/api/deals/DealEstimationHistory";
import { getDownloadPath as getHistoryDownloadPath } from "~/utils/api/deals/DealEstimationHistoryFile";
import {
	create as createDealEstimation,
	destroy as destroyDealEstimation,
	updateSeq as ownEstimationUpdateSeq,
	update as updateDealEstimation
} from "~/utils/api/deals/DealOwnEstimation";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const { notify } = useNotification();
	const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
	const [showAddModal, setShowAddModal] = useState(false);
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const {
		isOpen: isOpenCreate,
		handleOpen: handleOpenCreate,
		handleClose: handleCloseCreate
	} = useOpenHandler();
	const [isOpenOcr, setIsOpenOcr] = useState(false);
	const handleOpenOcr = useCallback(() => {
		setIsOpenOcr(true);
	}, []);
	const handleCloseOcr = useCallback(() => {
		setIsOpenOcr(false);
	}, []);
	const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
	const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
	const router = useRouter();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const { data: account } = useItandiAccount().currentAccount();
	const {
		data: workflowRestriction,
		error: workflowRestrictionError,
		isLoading: isWorkflowRestrictionLoading,
		mutate: mutateWorkflowRestriction
	} = useDealWorkflowRestriction(id);
	const { data: companyWorkflowSettings } = useCompanyWorkflowSettings();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: dealConstructionItems } = useDealConstructionItems();
	const { data: estimationContext, mutate: mutateEstimationContext } =
		useDealEstimations().index(id);
	const {
		data: dealEstimationHistoryContext,
		mutate: mutateEstimationHistoryContext
	} = useDealEstimationHistories().index(id);
	const { data: dealEstimationHeaders, mutate: mutateHeader } =
		useDealEstimationHeaders().index(id);
	const requestHeader =
		dealEstimationHeaders?.find((header) => header.status === "request") ??
		null;
	const { data: requestHistoryContext, isLoading } =
		useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
	const dealEstimationHeaderId =
		dealEstimationHistoryContext?.dealEstimationHeaderId ?? null;
	useEffect(() => {
		if (deal === null) return;
		if (!isOwnStatus(deal.statusId) && !estimationRequestSide(deal, "client")) {
			router.replace(ESTIMATIONS_PATH(deal.id)).then();
		}
	}, [deal, router]);
	if (
		deal === null ||
		isLoading ||
		account === null ||
		estimationContext === null ||
		dealEstimationHistoryContext === null ||
		dealEstimationHeaders === null ||
		dealConstructionItems === null
	) {
		return <Loading isLoading />;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "見積り作成" }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];
	const ownManagement = isOwnStatus(deal.statusId);
	const showRequestFooter =
		canEstimationRequestStatus(deal.statusId) &&
		(dealEstimationHistoryContext?.dealConstructions.length !== 0 ||
			requestHeader !== null);
	const constructionAddDisabled = checkConstructionAddDisabled(
		"estimation",
		requestHeader,
		deal
	);
	const buildEstimationDownloadPath = (): string => {
		if (dealEstimationHeaderId) {
			return getHistoryDownloadPath(deal.id, dealEstimationHeaderId);
		}
		return getDownloadPath(deal.id);
	};
	const handleClickPreview = (): void => {
		const path = buildEstimationDownloadPath();
		window.open(path);
	};
	const buildHistoryDownloadPath = (): string => {
		// 申請前(draftのheader)
		if (dealEstimationHeaderId) {
			return getHistoryDownloadPath(deal.id, dealEstimationHeaderId);
		}
		// 申請中(requestのheader)
		if (requestHeader) {
			return getHistoryDownloadPath(deal.id, requestHeader.id);
		}
		// どちらもないとき
		return "";
	};
	const handleClickHistoryPreview = (): void => {
		const path = buildHistoryDownloadPath();
		window.open(path);
	};
	const handleClickShowAddModal = (): void => {
		setShowAddModal(true);
	};
	const handleMutateEstimationHistories = async (): Promise<void> => {
		await mutateDeal?.();
		await mutateEstimationContext?.();
		await mutateEstimationHistoryContext?.();
		await mutateWorkflowRestriction();
	};
	const handleMutateRequestHeader = async (): Promise<void> => {
		await mutateDeal?.();
		await mutateHeader?.();
	};
	const handleCreate = async (
		submitValue: DealConstructionEdit
	): Promise<NskResponse> => {
		if (ownManagement) return createDealEstimation(submitValue, deal.id);
		return postDealEstimationHistory(submitValue, deal.id, null, null);
	};
	const handleUpdate = async (
		submitValue: DealConstructionEdit,
		estimationId: number | null,
		historyId: number | null
	): Promise<NskResponse> => {
		if (ownManagement && estimationId !== null) {
			return updateDealEstimation(submitValue, estimationId, deal.id);
		}
		if (estimationId !== null) {
			return postDealEstimationHistory(
				submitValue,
				deal.id,
				estimationId,
				null
			);
		}
		if (historyId === null) {
			throw new Error("historyId cannot be null");
		}
		return postDealEstimationHistory(submitValue, deal.id, null, historyId);
	};

	const handleDestroy = async (
		dealConstruction: DealConstruction
	): Promise<NskResponse> => {
		if (ownManagement && dealConstruction.type === "DealEstimation") {
			return destroyDealEstimation(dealConstruction.id, deal.id);
		}
		if (dealConstruction.type === "DealBilling") {
			throw new Error("deleteEstimation.type cannot be DealBilling");
		}
		if (dealConstruction.type === "DealEstimationHistory") {
			if (dealConstruction.operationType === "add") {
				// そのとき新規追加したものはデータを消す
				return destroyEstimationHistory(deal.id, dealConstruction.id);
			}
			if (dealConstruction.dealEstimation === null) {
				throw new Error("dealEstimation cannot be null");
			}
			// すでに承認されていたものは、申請を通じて削除する
			return updateTypeDelete(deal.id, dealConstruction.dealEstimation.id);
		}
		// すでに承認されていたものは、申請を通じて削除する
		return updateTypeDelete(deal.id, dealConstruction.id);
	};
	const handleUpdateSeq = async (
		newEstimations: DealConstruction[]
	): Promise<void> => {
		if (newEstimations.length === 0) {
			throw new Error("newEstimations.length cannot be 0");
		}
		if (newEstimations[0].type === "DealEstimation") {
			if (ownManagement) {
				await ownEstimationUpdateSeq(newEstimations, deal.id);
				return;
			}
			await estimationUpdateSeq(newEstimations, deal.id);
			return;
		}
		await historyUpdateSeq(newEstimations, deal.id);
	};
	const handleUpdateRequestStatus = async (): Promise<NskResponse> => {
		if (dealEstimationHeaderId === null) {
			throw new Error("dealEstimationHeaderId cannot be null");
		}
		return updateRequest(deal.id, dealEstimationHeaderId);
	};
	const handleUpdateCancelStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return update(deal.id, requestHeader.id, "cancel");
	};
	const handleClickCreateHeader = async (): Promise<void> => {
		const result = await create(deal.id);
		if (isSuccess(result)) {
			await mutateEstimationHistoryContext?.();
			await mutateDeal?.();
			notify({ content: "見積りを複製しました。", variant: "success" });
			handleCloseCreate();
			return;
		}
		handleCloseCreate();
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	const handleClickDestroyHeader = async (): Promise<void> => {
		if (dealEstimationHeaderId === null) {
			throw new Error("dealEstimationHeaderId cannot be null");
		}
		const result = await destroy(deal.id, dealEstimationHeaderId);
		if (isSuccess(result)) {
			await mutateEstimationHistoryContext?.();
			await mutateDeal?.();
			notify({
				content: "編集中の見積りを元に戻しました。",
				variant: "success"
			});
			handleClose();
			return;
		}
		handleClose();
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	const showHighlight = ((): boolean => {
		// 非共有案件はハイライトを見せない
		if (ownManagement) return false;
		// deal_estimation_headerが存在してない時はハイライトを見せない
		if (
			dealEstimationHistoryContext.dealConstructions.length === 0 &&
			!requestHeader
		)
			return false;
		return true;
	})();
	const isEstimationDocumentDownloadable =
		!isWorkflowRestrictionLoading &&
		!workflowRestrictionError &&
		(workflowRestriction?.estimation.downloadable ?? false);
	const isEstimationLocked =
		isWorkflowRestrictionLoading ||
		!!workflowRestrictionError ||
		(workflowRestriction?.estimation.locked ?? true);
	const workflowLockMessage =
		isWorkflowRestrictionLoading ||
		workflowRestrictionError ||
		workflowRestriction === null
			? null
			: buildWorkflowLockMessage(
					workflowRestriction.estimation,
					WORKFLOW_LOCK_TARGET_LABEL[WORKFLOW_RESOURCE_TYPE.ESTIMATION]
				);
	const isVendorEstimation =
		!ownManagement && deal.estimationBy === ESTIMATION_BY.VENDOR;
	const isReadonly = isVendorEstimation || isEstimationLocked;
	const estimationStatus = estimationHeaderStatus(deal);
	if (!windowLoaded) return null;
	return (
		<DefaultV2>
			<ClientDealLayout deal={deal} enableChat items={items}>
				<Flex justifyContent="space-between">
					<Flex alignItems="center" gap={designTokens["spacing/s"]}>
						<h1 className={Styles.EstimateTitle}>見積り作成</h1>
						{estimationStatus.label !== "" &&
							estimationStatus.variant !== null && (
								<PresentationChip variant={estimationStatus.variant}>
									{estimationStatus.label}
								</PresentationChip>
							)}
					</Flex>
					{!isSp ? (
						<Flex gap={designTokens["spacing/xs"]}>
							{!isReadonly ? (
								<Button
									disabled={constructionAddDisabled}
									onClick={handleOpenOcr}
									startIcon={<RiSparklingLine />}
									variant="secondary"
								>
									AIで明細を読み取る
								</Button>
							) : null}
							<ConstructionDownloadButtons
								constructionContext={estimationContext}
								dealEstimationDraftHeader={dealEstimationHistoryContext}
								dealEstimationHistoryContext={historyConstructionContext(
									requestHistoryContext,
									dealEstimationHistoryContext
								)}
								disabled={!isEstimationDocumentDownloadable}
								handleClickEstimationHistoryPreview={
									buildHistoryDownloadPath() === ""
										? null
										: handleClickHistoryPreview
								}
								handleClickEstimationPreview={handleClickPreview}
								ownManagement={ownManagement}
							/>
							{canStartWorkflow(
								companyWorkflowSettings,
								WORKFLOW_RESOURCE_TYPE.ESTIMATION
							) ? (
								<Button
									disabled={
										isWorkflowRestrictionLoading ||
										!!workflowRestrictionError ||
										!workflowRestriction?.estimation.startable
									}
									onClick={() => {
										setIsWorkflowModalOpen(true);
									}}
									variant="primary"
								>
									ワークフローを開始
								</Button>
							) : null}
							{workflowRestriction?.estimation.locked ? (
								<Button
									disabled={!workflowRestriction.estimation.unlockable}
									onClick={() => {
										setIsUnlockModalOpen(true);
									}}
									variant="secondary"
								>
									編集ロックを解除
								</Button>
							) : null}
						</Flex>
					) : null}
				</Flex>
				{estimationHeaderStatus(deal).description !== "" &&
					deal.statusId !== IN_ESTIMATION && (
						<Grid
							marginTop={designTokens["spacing/s"]}
							paddingX={isSp ? 16 : 0}
							width="fit-content"
						>
							<Alert>{estimationHeaderStatus(deal).description}</Alert>
						</Grid>
					)}
				{deal.statusId === IN_ESTIMATION && (
					<Grid
						marginTop={designTokens["spacing/s"]}
						paddingX={isSp ? 16 : 0}
						width="fit-content"
					>
						<Alert>
							初めて見積確認中ステータスに移行する場合、変更前や削除した見積情報の編集履歴を施工会社は確認できません。
						</Alert>
					</Grid>
				)}
				{workflowLockMessage !== null ? (
					<Grid
						marginTop={designTokens["spacing/s"]}
						paddingX={isSp ? 16 : 0}
						width="fit-content"
					>
						<Alertv2 variant="warning">
							<Paragraph>{workflowLockMessage}</Paragraph>
						</Alertv2>
					</Grid>
				) : null}
				<ConstructionTop
					constructionContext={estimationContext}
					dealEstimationDraftHeader={dealEstimationHistoryContext}
					dealEstimationHistoryContext={historyConstructionContext(
						requestHistoryContext,
						dealEstimationHistoryContext
					)}
					estimationAddDisabled={requestHeader !== null}
					handleClickCreateHeader={
						deal.latestEstimationRejected && !isEstimationLocked
							? handleOpenCreate
							: null
					}
					handleClickDestroyHeader={isEstimationLocked ? null : handleOpen}
					handleClickShowAddModal={handleClickShowAddModal}
					ownManagement={ownManagement}
					readonly={isReadonly}
					showSubtotal
				/>
				<ConfirmModal
					confirmType="save"
					handleAccept={handleClickCreateHeader}
					handleCancel={handleCloseCreate}
					isOpen={isOpenCreate}
				>
					棄却された見積りを元に見積りを複製しますか？
				</ConfirmModal>
				<ConfirmModal
					confirmType="save"
					handleAccept={handleClickDestroyHeader}
					handleCancel={handleClose}
					isOpen={isOpen}
				>
					現在編集中の見積りを元に戻しますか？
					<Alert marginTop=".5rem">
						既に反映されている見積りの状態に戻ります
					</Alert>
				</ConfirmModal>
				<div className={Styles.Container}>
					{!isReadonly && (
						<div className={Styles.EstimateAddForm}>
							<ConstructionAddForm
								dealConstructionItems={dealConstructionItems}
								disabled={constructionAddDisabled}
								docType="estimation"
								handleCreate={handleCreate}
								mutateConstructions={handleMutateEstimationHistories}
							/>
						</div>
					)}
					<div className={Styles.EstimateList}>
						<ConstructionList
							companyType="client"
							constructionContext={estimationConstructionContext(
								requestHistoryContext,
								dealEstimationHistoryContext,
								estimationContext
							)}
							deal={deal}
							dealConstructionItems={dealConstructionItems}
							docType="estimation"
							handleDestroy={handleDestroy}
							handleMutateConstructions={handleMutateEstimationHistories}
							handleUpdate={handleUpdate}
							handleUpdateSeq={
								requestHeader === null && !isEstimationLocked
									? handleUpdateSeq
									: null
							}
							readonly={isReadonly}
							requestHeader={requestHeader}
							showHighlight={showHighlight}
						/>
						{dealEstimationHistoryContext && showRequestFooter ? (
							<ConstructionRequestFooter
								companyType="client"
								deal={deal}
								dealEstimationHistoryContext={dealEstimationHistoryContext}
								disabled={isEstimationLocked}
								handleMutate={handleMutateEstimationHistories}
								handleMutateRequestHeader={handleMutateRequestHeader}
								handleUpdateCancelStatus={handleUpdateCancelStatus}
								handleUpdateRequestStatus={handleUpdateRequestStatus}
								isAdmin={account.isAdmin}
								requestHeader={requestHeader}
							/>
						) : (
							<div className={Styles.CompleteButtonArea}>
								<Button
									onClick={(): void => {
										router.push(PATH(deal.id)).then();
									}}
									variant="primary"
								>
									完了して戻る
								</Button>
							</div>
						)}
					</div>
				</div>
				{showAddModal ? (
					<ConstructionAddModal
						dealConstructionItems={dealConstructionItems}
						disabled={constructionAddDisabled}
						handleClose={(): void => {
							setShowAddModal(false);
						}}
						handleCreate={handleCreate}
						mutateConstructions={handleMutateEstimationHistories}
					/>
				) : null}
				{isOpenOcr ? (
					<DealEstimationOcrModal
						dealId={deal.id}
						handleClose={handleCloseOcr}
					/>
				) : null}
				{isUnlockModalOpen ? (
					<WorkflowLockUnlockModal
						dealId={deal.id}
						handleClose={() => {
							setIsUnlockModalOpen(false);
						}}
						resourceType={WORKFLOW_RESOURCE_TYPE.ESTIMATION}
					/>
				) : null}
				{isWorkflowModalOpen ? (
					<WorkflowStartModal
						companyWorkflowSettings={companyWorkflowSettings!}
						dealId={deal.id}
						handleClose={() => {
							setIsWorkflowModalOpen(false);
						}}
						resourceType={WORKFLOW_RESOURCE_TYPE.ESTIMATION}
					/>
				) : null}
			</ClientDealLayout>
		</DefaultV2>
	);
};
